import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Button, Input, Modal, message, Space, Table } from 'antd';
import {
    SaveOutlined,
    EditOutlined,
    StopOutlined,
    DownloadOutlined,
    UploadOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import AppLayout from '../../../components/layout/AppLayout';
import JsonEditor from '../../../components/JsonEditor/JsonEditor';
import { useTranslateApi } from '../../../api/translate/translate';
import { TTranslation } from '../../../models/translation/translation';

const TranslateList: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { getTranslations, saveTranslations } = useTranslateApi();

    const [translations, setTranslations] = useState<TTranslation[]>([]);
    const [editingIndex, setEditingIndex] = useState<number | null>(null);
    const [editingTranslation, setEditingTranslation] = useState<TTranslation | null>(null);

    const [loading, setLoading] = useState<boolean>(false);
    const [loadingRow, setLoadingRow] = useState<number | null>(null);
    const [importOpen, setImportOpen] = useState(false);

    useEffect(() => {
        setLoading(true);
        getTranslations()
            .then((resp) => {
                setTranslations(resp.data);
            })
            .catch((err) => {
                message.error(err.response?.data ?? err.message);
            })
            .finally(() => {
                setLoading(false);
            });
        // eslint-disable-next-line
    }, []);

    // Export : toutes les traductions (forme actuelle, on garde tout l'objet)
    const exportToJson = () => {
        const dataStr = JSON.stringify(translations, null, 2);
        const dataUri = 'data:application/json;charset=utf-8,' + encodeURIComponent(dataStr);

        const exportFileDefaultName = 'translations-all.json';

        const linkElement = document.createElement('a');
        linkElement.setAttribute('href', dataUri);
        linkElement.setAttribute('download', exportFileDefaultName);
        linkElement.click();

        message.success('Translations exported successfully!');
    };

    // Export : uniquement les lignes non complètement traduites (EN ou AR manquante)
    const exportUntranslatedToJson = () => {
        const untranslated = translations.filter(
            (tr) => !tr.label_en || !tr.label_ar
        );

        if (untranslated.length === 0) {
            message.info('All labels are fully translated.');
            return;
        }

        const dataStr = JSON.stringify(untranslated, null, 2);
        const dataUri = 'data:application/json;charset=utf-8,' + encodeURIComponent(dataStr);

        const exportFileDefaultName = 'translations-untranslated.json';

        const linkElement = document.createElement('a');
        linkElement.setAttribute('href', dataUri);
        linkElement.setAttribute('download', exportFileDefaultName);
        linkElement.click();

        message.success('Untranslated items exported successfully!');
    };

    // Import JSON (full ou partiel) : on envoie seulement ce qui est importé,
    // le backend upsert par label_fr, puis on recharge la liste complète.
    const handleImport = (data: any) => {
        try {
            if (!Array.isArray(data)) {
                message.error('Invalid JSON format. Expected an array of objects.');
                return;
            }

            const isValid = data.every(
                (item) =>
                    typeof item === 'object' &&
                    item !== null &&
                    'label_fr' in item
            );

            if (!isValid) {
                message.error(
                    'Invalid JSON structure. Each object must contain at least label_fr (and optionally label_en, label_ar).'
                );
                return;
            }

            // On prépare les données pour le backend : il fera l’UPSERT sur label_fr.
            const processedData: TTranslation[] = data.map((item: any) => ({
                id: item.id ?? 0, // backend s'en fiche, il se base sur label_fr
                label_fr: item.label_fr,
                label_en: item.label_en ?? '',
                label_ar: item.label_ar ?? '',
            }));

            setLoading(true);
            saveTranslations(processedData)
                .then(() => {
                    message.success('Translations imported successfully!');
                    // On recharge la liste complète pour être sûr d’être aligné avec la base
                    return getTranslations();
                })
                .then((resp) => {
                    setTranslations(resp.data);
                    setImportOpen(false);
                })
                .catch((err) => {
                    message.error(err.response?.data ?? err.message);
                })
                .finally(() => {
                    setLoading(false);
                });
        } catch (error) {
            message.error('Error parsing JSON data. Please check the format.');
        }
    };

    // Fonction pour entrer en mode édition ligne par ligne
    const edit = (index: number, translation: TTranslation) => {
        setEditingIndex(index);
        setEditingTranslation({ ...translation });
    };

    // Sauvegarder une ligne
    const save = (index: number) => {
        if (editingTranslation) {
            setLoadingRow(index);
            saveTranslations([editingTranslation])
                .then(() => {
                    const updatedTranslations = [...translations];
                    updatedTranslations[index] = editingTranslation;
                    setTranslations(updatedTranslations);
                    message.success(t('message.savedSuccessfully'));
                })
                .catch((err) => {
                    message.error(err.response?.data ?? err.message);
                })
                .finally(() => {
                    setLoadingRow(null);
                });
        }
        setEditingIndex(null);
        setEditingTranslation(null);
    };

    const cancel = () => {
        setEditingIndex(null);
        setEditingTranslation(null);
    };

    const columns: ColumnsType<TTranslation> = [
        {
            title: t('admin.translate.tableHeaders.labelFr'),
            dataIndex: 'label_fr',
            key: 'label_fr',
            render: (text: string) => text,
        },
        {
            title: t('admin.translate.tableHeaders.labelEn'),
            dataIndex: 'label_en',
            key: 'label_en',
            render: (text: string, _record: TTranslation, index: number) =>
                editingIndex === index ? (
                    <Input
                        value={editingTranslation?.label_en}
                        onChange={(e) => {
                            if (editingTranslation) {
                                setEditingTranslation({
                                    ...editingTranslation,
                                    label_en: e.target.value,
                                });
                            }
                        }}
                    />
                ) : (
                    text
                ),
        },
        {
            title: t('admin.translate.tableHeaders.labelAr'),
            dataIndex: 'label_ar',
            key: 'label_ar',
            render: (text: string, _record: TTranslation, index: number) =>
                editingIndex === index ? (
                    <Input
                        value={editingTranslation?.label_ar}
                        onChange={(e) => {
                            if (editingTranslation) {
                                setEditingTranslation({
                                    ...editingTranslation,
                                    label_ar: e.target.value,
                                });
                            }
                        }}
                    />
                ) : (
                    text
                ),
        },
        {
            title: t('admin.translate.tableHeaders.actions'),
            key: 'actions',
            fixed: 'right' as 'right',
            width: 180,
            render: (_: any, record: TTranslation, index: number) => (
                <Space size="middle">
                    {editingIndex === index ? (
                        <>
                            <Button
                                icon={<SaveOutlined />}
                                onClick={() => save(index)}
                                loading={loadingRow === index}
                            />
                            <Button
                                icon={<StopOutlined />}
                                onClick={cancel}
                                disabled={loadingRow === index}
                            />
                        </>
                    ) : (
                        <>
                            <Button
                                icon={<EditOutlined />}
                                onClick={() => edit(index, record)}
                            />
                        </>
                    )}
                </Space>
            ),
        },
    ];

    const handleBackNavigation = () => {
        navigate(-1);
    };

    return (
        <AppLayout
            title={t('admin.translate.title')}
            onBackNavigation={handleBackNavigation}
            showBackButton={true}
        >
            <div>
                <Space style={{ marginBottom: 16 }}>
                    <Button
                        type="primary"
                        icon={<UploadOutlined />}
                        onClick={() => setImportOpen(true)}
                    >
                        {t('admin.translate.importJson')}
                    </Button>
                    <Button icon={<DownloadOutlined />} onClick={exportToJson}>
                        {t('admin.translate.exportJson')}
                    </Button>
                    <Button
                        icon={<DownloadOutlined />}
                        onClick={exportUntranslatedToJson}
                    >
                        {t('admin.translate.exportUntranslatedJson')}
                    </Button>
                </Space>

                <Modal
                    title={t('admin.translate.modalTitle')}
                    open={importOpen}
                    onCancel={() => setImportOpen(false)}
                    footer={null}
                    width={800}
                    destroyOnHidden
                >
                    <JsonEditor
                        title={t('admin.translate.jsonEditorTitle')}
                        value={
                            translations.length > 0
                                ? translations.map(({ id, ...rest }) => rest)
                                : [
                                    {
                                        label_fr: t('admin.translate.exampleText'),
                                        label_en: t('admin.translate.exampleText'),
                                        label_ar: t('admin.translate.exampleText'),
                                    },
                                ]
                        }
                        onApply={handleImport}
                    />
                </Modal>

                <Table<TTranslation>
                    columns={columns}
                    dataSource={translations}
                    rowKey={(record) =>
                        record.id
                            ? record.id.toString()
                            : `temp-${record.label_fr}`
                    }
                    pagination={{ pageSize: 20 }}
                    scroll={{ x: 800 }}
                    loading={loading}
                />
            </div>
        </AppLayout>
    );
};

export default TranslateList;
